/**
 * Fetch a single gateway deployment with targets (and release info).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { GatewayDeployment, GatewayDeploymentTarget } from '@/types/gateway';

export function useGatewayDeployment(deploymentId: string | null) {
  const query = useQuery({
    queryKey: ['gateway-deployment', deploymentId],
    queryFn: async (): Promise<{ deployment: GatewayDeployment; targets: GatewayDeploymentTarget[] } | null> => {
      if (!deploymentId) return null;

      const { data: depRow, error: depErr } = await supabase
        .from('gateway_deployments')
        .select(`
          id,
          organization_id,
          firmware_release_id,
          name,
          strategy,
          staged_config,
          maintenance_window,
          status,
          total_gateways,
          updated_gateways,
          failed_gateways,
          failure_threshold_pct,
          created_at,
          started_at,
          completed_at,
          paused_at,
          created_by,
          gateway_firmware_releases(version, release_notes)
        `)
        .eq('id', deploymentId)
        .single();

      if (depErr || !depRow) return null;

      const d = depRow as Record<string, unknown>;
      const release = d.gateway_firmware_releases as { version: string; release_notes: string | null } | null;

      const deployment: GatewayDeployment = {
        id: d.id as string,
        organization_id: d.organization_id as string,
        firmware_release_id: d.firmware_release_id as string,
        name: d.name as string | null,
        strategy: d.strategy as GatewayDeployment['strategy'],
        staged_config: d.staged_config as GatewayDeployment['staged_config'],
        maintenance_window: d.maintenance_window as GatewayDeployment['maintenance_window'],
        status: d.status as GatewayDeployment['status'],
        total_gateways: (d.total_gateways as number) ?? 0,
        updated_gateways: (d.updated_gateways as number) ?? 0,
        failed_gateways: (d.failed_gateways as number) ?? 0,
        failure_threshold_pct: (d.failure_threshold_pct as number) ?? 20,
        created_at: d.created_at as string,
        started_at: d.started_at as string | null,
        completed_at: d.completed_at as string | null,
        paused_at: d.paused_at as string | null,
        created_by: d.created_by as string | null,
        firmware_version: release?.version,
        release_notes: release?.release_notes ?? undefined,
      };

      const { data: targetRows, error: targetsErr } = await supabase
        .from('gateway_deployment_targets')
        .select(`
          id,
          deployment_id,
          gateway_id,
          stage,
          status,
          started_at,
          completed_at,
          error,
          previous_version,
          gateway_devices(mac_address, properties(name))
        `)
        .eq('deployment_id', deploymentId)
        .order('stage')
        .order('status');

      if (targetsErr) return { deployment, targets: [] };

      const targets: GatewayDeploymentTarget[] = (targetRows ?? []).map((t: Record<string, unknown>) => {
        const gd = t.gateway_devices as { mac_address: string | null; properties: { name: string } | null } | null;
        return {
          id: t.id as string,
          deployment_id: t.deployment_id as string,
          gateway_id: t.gateway_id as string,
          stage: (t.stage as number) ?? 1,
          status: t.status as GatewayDeploymentTarget['status'],
          started_at: t.started_at as string | null,
          completed_at: t.completed_at as string | null,
          error: t.error as string | null,
          previous_version: t.previous_version as string | null,
          gateway_mac_address: gd?.mac_address ?? null,
          property_name: gd?.properties?.name ?? null,
        };
      });

      return { deployment, targets };
    },
    enabled: !!deploymentId,
    staleTime: 10_000,
  });

  return {
    deployment: query.data?.deployment ?? null,
    targets: query.data?.targets ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
